import activityService from '../services/activityService';
import { ACTIVITY_ACTION_TYPES } from './types';
import {
  getActivityNotificationStableId,
  rawActivityNotificationLooksUnread,
  resolveActivityNotificationViewIds,
  normalizeArchivedActivityPagination,
} from '../utils/activityUtil';
import { getFetchableActivityTabIds } from '../constants/activity';
import { fetchNotificationCount } from './notificationAction';

function findActivityRowForView(state, tabId, grouped, stableId) {
  const trySlice = (g) => {
    const key = String(!!g);
    const slice = state.activity?.byTab?.[tabId]?.byGrouped?.[key];
    const row = slice?.items?.find(
      (x) => getActivityNotificationStableId(x) === stableId
    );
    return row ? { row, grouped: !!g } : null;
  };

  const primary = trySlice(!!grouped);
  if (primary) return primary;
  return trySlice(!grouped);
}

/**
 * Fetch one creator activity tab. Dispatches immediately; each tab updates Redux when its response arrives.
 * @param {string} tabId
 * @param {{ page?: number, limit?: number, append?: boolean, role?: number, grouped?: boolean }} options
 */
export const fetchCreatorActivityTab = (tabId, options = {}) => {
  return async (dispatch) => {
    const {
      page = 1,
      limit = 20,
      append = false,
      grouped = true,
      archived = false,
    } = options;

    dispatch({
      type: ACTIVITY_ACTION_TYPES.TAB_REQUEST,
      payload: { tabId, grouped, archived, page },
    });

    try {
      const res = await activityService.getActivityNotifications(
        tabId,
        { page, limit, grouped: !!grouped, archived: !!archived },
        options.role
      );

      if (!res.success) {
        throw new Error(res.message || 'Failed to load activity');
      }

      const data = res.data || {};
      let pagination = data.pagination || null;
      if (archived) {
        pagination = normalizeArchivedActivityPagination(pagination, { page, limit });
      }

      dispatch({
        type: ACTIVITY_ACTION_TYPES.TAB_SUCCESS,
        payload: {
          tabId,
          grouped,
          archived,
          items: Array.isArray(data.items) ? data.items : [],
          pagination,
          unreadCount:
            typeof data.notificationUnreadCount === 'number'
              ? data.notificationUnreadCount
              : 0,
          append,
          page,
        },
      });
      return { success: true };
    } catch (err) {
      const message =
        err?.message ||
        err?.error?.message ||
        'Failed to load activity';
      dispatch({
        type: ACTIVITY_ACTION_TYPES.TAB_FAILURE,
        payload: { tabId, grouped, archived, error: message },
      });
      return { success: false, error: message };
    }
  };
};

/**
 * PATCH viewed endpoint for one notification (or each id in a grouped tile's unreadIds).
 * On success updates Redux so isRead is true without refetch.
 * @param {string} tabId
 * @param {boolean} grouped - must match the slice where the item lives (Activity page "All" vs single tab).
 * @param {string} stableId - getActivityNotificationStableId / list item id
 * @param {string|string[]|null} apiNotificationIdOrIds - raw _id or grouped unreadIds; omitted to resolve from Redux row
 * @param {number} role
 */
export const markActivityNotificationViewed = (
  tabId,
  grouped,
  stableId,
  apiNotificationIdOrIds,
  role
) => {
  return async (dispatch, getState) => {
    if (!stableId || tabId === 'archived') {
      return { success: false };
    }

    const state = getState();
    const found = findActivityRowForView(state, tabId, grouped, stableId);
    if (!found) {
      return { success: false };
    }
    const { row, grouped: resolvedGrouped } = found;
    const wasUnread =
      row.isRead === false ||
      row.isViewed === false ||
      row.read === false ||
      row.viewed === false ||
      row.is_read === false ||
      row.is_viewed === false;
    if (!wasUnread) {
      return { success: true, skipped: true };
    }

    let viewIds = [];
    if (Array.isArray(apiNotificationIdOrIds)) {
      viewIds = apiNotificationIdOrIds
        .map((id) => (id == null ? '' : String(id).trim()))
        .filter(Boolean);
    } else if (apiNotificationIdOrIds) {
      viewIds = [String(apiNotificationIdOrIds).trim()].filter(Boolean);
    }
    if (!viewIds.length) {
      viewIds = resolveActivityNotificationViewIds(row);
    }
    if (!viewIds.length) {
      return { success: false };
    }

    try {
      for (const notificationId of viewIds) {
        const res = await activityService.markNotificationViewed(
          tabId,
          notificationId,
          role
        );
        if (!res.success) {
          throw new Error(res.message || 'Failed to mark viewed');
        }
      }
      dispatch({
        type: ACTIVITY_ACTION_TYPES.ITEM_VIEWED,
        payload: {
          tabId,
          grouped: resolvedGrouped,
          stableId,
          decrementUnread: wasUnread,
        },
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: err?.message };
    }
  };
};

/**
 * PATCH viewed for every unread row already loaded in Redux (all activity tabs except archive).
 * Run after Activity loads so visiting Activity clears server unread state for visible notifications.
 */
export const markAllLoadedActivityNotificationsViewed = (role) => {
  return async (dispatch, getState) => {
    const tabIds = getFetchableActivityTabIds(role).filter((id) => id !== 'archived');

    /** @type {{ tabId: string, grouped: boolean, stableId: string, viewIds: string[] }[]} */
    const queued = [];

    for (const tabId of tabIds) {
      const byGrouped = getState().activity?.byTab?.[tabId]?.byGrouped;
      if (!byGrouped) continue;

      const groupedKeys = ['true', 'false'];

      for (const groupedKey of groupedKeys) {
        const slice = byGrouped[groupedKey];
        if (!slice?.items?.length) continue;
        const grouped = groupedKey === 'true';
        for (const row of slice.items) {
          if (!rawActivityNotificationLooksUnread(row)) continue;
          const stableId = getActivityNotificationStableId(row);
          const viewIds = resolveActivityNotificationViewIds(row);
          if (!stableId || !viewIds.length) continue;
          queued.push({ tabId, grouped, stableId, viewIds });
        }
      }
    }

    if (!queued.length) {
      return { success: true, marked: 0 };
    }

    for (const q of queued) {
      await dispatch(
        markActivityNotificationViewed(
          q.tabId,
          q.grouped,
          q.stableId,
          q.viewIds,
          role
        )
      );
    }

    dispatch({ type: ACTIVITY_ACTION_TYPES.RESET_UNREAD_COUNTS });
    await dispatch(fetchNotificationCount({ skipActivitySync: true }));

    return { success: true, marked: queued.length };
  };
};
